import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getCredentials,
  removeCredentials,
  setCredentials,
} from "../../utils/helpers";
import { TLogin, TUser } from "../../@types/common";
import { useLocalStorage } from "../../hooks/common/useLocalStorage";
import { ONBOARD, USER } from "../../constants/constants";
import { App } from "antd";
import { checkIsOnbarded } from "../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext<any>({});

const useAuth = (): {
  loggedIn: any;
  logout: any;
  authToken: string;
  login: (options: TLogin) => void;
  updateUser: (data: any) => void;
  user: TUser;
} => useContext(AuthContext);

const AuthProvider = ({ children }: any) => {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [loggedIn, setLoggedIn]: any = useState(null);
  const [user, setUser]: any = useState(null);
  const [authToken, setAuthToken] = useState();
  const { getData, setData } = useLocalStorage();

  useEffect(() => {
    const fetchCredentials = () => {
      const credentials = getCredentials();
      const userData = getData(USER);
      if (credentials) {
        const token = credentials.access;
        setAuthToken(token);
        setUser(userData);
        setLoggedIn(true);
      } else {
        setLoggedIn(false);
      }
    };

    fetchCredentials();
  }, []);

  const login = async ({ credentials, afterLogin }: TLogin) => {
    setLoggedIn(null); // loading status
    const token = credentials.access;
    setCredentials(credentials);
    setAuthToken(token);

    if (afterLogin) {
      afterLogin({
        data: credentials,
        onSuccess: (data: any) => {
          setLoggedIn(true);

          // SAVE USER
          updateUser(data);
          message.success("Login Successful");

          // CHECK ACCOUNT
          if (!checkIsOnbarded(data)) {
            setTimeout(() => navigate(ONBOARD), 10);
          }
        },
      });
    }
  };

  const logout = () => {
    // Best-effort server-side logout: blacklist the refresh token so it
    // can't mint new access tokens. Local sign-out proceeds regardless.
    const refresh = getCredentials()?.refresh;
    if (refresh) {
      import("axios").then(async ({ default: axios }) => {
        const { BASE_URL } = await import("../../constants/config");
        const { API_LOGOUT } = await import("../../constants/apis");
        axios.post(`${BASE_URL}/${API_LOGOUT}`, { refresh }).catch(() => {});
      });
    }
    removeCredentials();
    setUser(null);
    setLoggedIn(false);
  };

  const updateUser = (data: any) => {
    setData(USER, data);
    setUser(data);
  };

  const renderBody = (ele: any) => cloneElement(ele);

  return (
    <AuthContext.Provider
      value={{
        login,
        loggedIn,
        logout,
        user,
        authToken,
        updateUser,
      }}
    >
      {renderBody(children)}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
